import {
  type ChatQuestion,
  type ClaimPart,
  INTAKE_SUMMARY,
  RECIPIENT,
} from '@/karehero-app/tell-us-once/intake/content';

/** What Keni's bubble says while the help answer is read, one line at a time. */
export const HELP_READING_LINES = [
  "Going through what you've shared",
  'Taking it all in',
  'Working out how we can help you',
];

/**
 * Onboarding 2.0: what they need help with, first.
 *
 * Built on 1.1. The chat opens on what they want from us before it asks who
 * they are, so the first thing they do is say why they came, and everything
 * after it can be read against that.
 *
 * Talked or typed rather than picked from a list: it is the one question whose
 * answer is theirs to frame. Read with everything else after they finish, so
 * nothing branches on it yet.
 *
 * Answered once, then read straight away. Nothing else is asked yet.
 */
const HELP: ChatQuestion = {
  id: 'help',
  // No section: the bar shows the logo, not the name of a set of questions.
  // One message: who is asking, what to tell us, and what they get for it.
  ask: "Hi, I'm Keni. What's going on at the moment? Tell me in your own words, and I'll show you how we can help.",
  // Prompts rather than a list in the message: the same card the 1.1 wait
  // uses, so the things they might say read as ours to offer, not more to read.
  card: {
    label: 'You could tell us about',
    sentence: true,
    items: [
      "Who you care for, even if it's yourself",
      'What caring for them involves',
      'What you need help with, like money and benefits or legal things',
    ],
    after: true,
  },
  // Once answered it is read, and said so in the chat a line at a time, long
  // enough for each line to be seen once before the summary arrives.
  reading: { lines: HELP_READING_LINES, ms: 6600 },
  // One answer, then it is read. No way on to press: answering is the way on.
  mode: 'say',
};

/**
 * What we heard, read back in the chat once the reading is done, rather than
 * on a review screen of its own. The summary is a card from us in the
 * transcript, and checking it is one more answer.
 */
const SUMMARY: ChatQuestion = {
  id: 'summary',
  // Said back as what we understood, not as what they told us: "You've told
  // us" reads like a statement being taken down. The one proper thank you in
  // the flow is here, after the hardest thing they share.
  says: ["Thank you for telling me all that. Here's what I've understood."],
  // The card's label is the question, so there is no bubble asking it as
  // well. The sparkle still marks the summary as something we produced.
  card: {
    label: INTAKE_SUMMARY.title,
    sentence: true,
    // Corrected in a drawer from the pencil, the edit pattern everywhere else.
    editable: true,
    // No names yet. A name heard in a voice note is a guess at its spelling,
    // so the summary says who they are to each other and the names are
    // asked for, spelled, once it is confirmed.
    items: [
      "You're caring for your mum, about 40 hours a week, with help from your sister.",
      "She's living with vascular dementia and osteoarthritis, walks with a frame, and needs help washing, dressing, eating and through the night.",
      "You'd like help with money she might be owed, and with care at home.",
    ],
  },
  ask: '',
  // The answer the harness fills it with when a later turn is opened.
  answer: "Yes, that's right",
  // The likely answers sit just above the text field, side by side, so
  // answering is a tap where the typing happens.
  quickReplies: [
    // Shorter than the review screen's, so both fit on one line.
    { label: "Yes, that's right" },
    { label: 'Not quite', secondary: true },
  ],
  // The composer stays: they can tap an answer, or type or say what is wrong.
  mode: 'say',
};

/**
 * How we can help, once the summary is confirmed: what the opening promised
 * ("I'll tell you how we can help"), built from what they said they want. A
 * card from us, one line per kind of help, in the order they asked for them.
 *
 * The "Yes, that's right" path. "Not quite" is not designed yet.
 */
const HOW_WE_HELP: ChatQuestion = {
  id: 'how-we-help',
  // Thanked once already, at the summary.
  says: ["Here's how we can help."],
  card: {
    label: 'How we can help',
    sentence: true,
    items: [
      'Money she might be owed. Attendance Allowance, a benefit for people who need care, could be £76.70 to £114.60 a week, and we fill in the form for you.',
      'Care at home. We can find carers near her and help you compare them.',
      'A care expert who knows your situation, to go through it with you.',
    ],
  },
  // Then the offer: a chip to tap, or their own words, typed or said.
  ask: 'Would you like help with any of this?',
  mode: 'say',
  quickReplies: [{ label: 'Yes' }, { label: 'Not right now', secondary: true }],
  // What the harness fills it with when a later turn is opened.
  answer: 'Yes',
};

/**
 * What we would need from them to do it: the next step once they say they
 * want help. Her name is asked for here, spelled as it is on her letters,
 * because the summary deliberately left it out. Theirs we already have from
 * signing up.
 *
 * The list is what the Attendance Allowance form and a care expert's call
 * need. Asking for each one is not designed yet.
 */
const WHAT_WE_NEED: ChatQuestion = {
  id: 'what-we-need',
  says: ["Here's what would help us get started."],
  card: {
    label: "What we'd need",
    sentence: true,
    // Ticked off as they send things, and kept in view at the top of the
    // chat until they say they are done or will send the rest later.
    checklistUntil: 'gather',
    items: [
      'Her full name, spelled as it is on her letters',
      'Her date of birth and National Insurance number',
      "Her GP, and any hospital stays or care she's had",
      'A phone number, so a care expert can call you',
    ],
  },
  // Two short chips above the field, which fit on one line; typed or said
  // works too.
  ask: 'Shall we make a start now?',
  mode: 'say',
  quickReplies: [
    { label: 'Yes' },
    { label: "I'll do it later", secondary: true },
  ],
  // What the harness fills it with when a later turn is opened.
  answer: 'Yes',
};

/**
 * After "Yes": they hand it over, in any order and any way, and say when they
 * are done. Stays open for as many messages and files as they like; the way
 * on is the I'm done chip, and saying they are done works too.
 */
const GATHER: ChatQuestion = {
  id: 'gather',
  // Nothing about giving us everything: when they are finished is theirs to
  // say.
  ask: "Send whatever you have, in any order. Say it, type it or add a photo of a letter. Tap I'm done when you're finished.",
  mode: 'say',
  multi: true,
  readsEach: true,
  // The way on as a chip above the field, like the answers elsewhere in 2.0.
  // A second way out for someone who has some of it now and the rest later.
  quickReplies: [
    { label: "I'm done", go: true },
    { label: "I'll send the rest later", go: true, secondary: true },
  ],
  // What the harness fills it with when a later turn is opened.
  answer: "I'm done",
};

/**
 * Once they are done: how far what they sent gets us, and which kind of help
 * to start on, as buttons attached under the message. The per cent is made up
 * for the prototype.
 */
const START: ChatQuestion = {
  id: 'start',
  // One message with the answers attached under it, WhatsApp-style.
  // Not starting any of it now is one of the quieter rows rather than a chip
  // above the field, so there is one place to tap.
  attachRows: {
    title: "That's plenty to get started",
    more: ['Learn more', "I'll do this later"],
  },
  ask: 'This gets us 60% of the way there. Where would you like to begin?',
  // Rows to tap, with the field kept under them: they can say or type it too.
  mode: 'say',
  answer: 'Claiming Attendance Allowance',
  // Picking one ends the conversation for now; doing it later gets a word
  // back first.
  next: { "I'll do this later": 'later-start', '*': 'end' },
  asides: [
    { label: 'Claiming Attendance Allowance' },
    { label: 'Finding care at home' },
    { label: 'Talking to a care expert' },
  ],
};

/**
 * The help question, the summary, then how we can help. The reading happens between them, as
 * its own screen: the help question is asked on its own, and the chat picks
 * up again at the summary.
 */
/** I'll do this later, to what to start with: no pressure, and that's it. */
const LATER_START: ChatQuestion = {
  id: 'later-start',
  ask: "That's fine. I'll be ready for you whenever you are.",
  mode: 'say',
};

export const INTAKE_CHAT_V2_0: ChatQuestion[] = [
  HELP,
  SUMMARY,
  HOW_WE_HELP,
  WHAT_WE_NEED,
  GATHER,
  START,
  // Off the main path, after its end.
  LATER_START,
];

/** The same, with I'll do this later picked, to open on what it says. */
export const INTAKE_CHAT_V2_0_LATER: ChatQuestion[] = INTAKE_CHAT_V2_0.map(
  (q) => (q.id === 'start' ? { ...q, answer: "I'll do this later" } : q),
);

/**
 * The dynamic concept's Home, opening the app again: a new conversation, the
 * way a chat app opens on an empty one. The greeting sits in the middle
 * (ChatV2's `greeting`), and the composer is the only thing to use.
 */
export const HOME_GREETING = 'Hello, welcome back. How can I help today?';

const HOME: ChatQuestion = {
  id: 'home',
  ask: '',
  mode: 'say',
};

/** Where we left off, from what onboarding heard and offered. */
const HOME_RECAP: ChatQuestion = {
  id: 'home-recap',
  says: [
    'Last time, you told me about looking after your mum. We worked out she could get Attendance Allowance, and we have enough to start the claim.',
  ],
  ask: 'Shall we carry on with it?',
  mode: 'say',
  quickReplies: [{ label: 'Yes' }, { label: 'Not right now', secondary: true }],
};

export const INTAKE_CHAT_V2_0_HOME: ChatQuestion[] = [HOME, HOME_RECAP];

/** The bar's menu on that Home: the conversations so far, the way a chat app lists them. */
export const HOME_HISTORY = {
  title: 'KareHero',
  newChat: 'New conversation',
  recent: 'Recent',
  threads: [{ title: 'Getting started' }],
};

/**
 * The second Home concept: what is coming up and the conversations, on the
 * page rather than behind a menu.
 */
export const HOME_CONCEPT_2 = {
  noCalls: 'No upcoming calls',
  bookCall: 'Book a call',
  bookCallAfter: ' with a care expert.',
  /**
   * What onboarding asked for and has not had: What we'd need, shortened to
   * a line each. Goes once all of it is in.
   */
  todo: 'Still to send',
  todos: [
    `${RECIPIENT}'s full name`,
    'Her date of birth and National Insurance number',
    'Her GP and any hospital stays',
    'Your phone number',
  ],
  conversations: 'Conversations',
};

/**
 * Concept 3.1: they start typing on Home, with the list still to send on it.
 * Keni works out whether what they said is one of those things. If it is, it
 * is ticked off. If Keni cannot tell, it asks, and offers to put the list
 * away: it can come back whenever they ask.
 */
const HOME_TYPING: ChatQuestion = {
  id: 'home',
  ask: '',
  mode: 'say',
  // Typed, so the replay shows their words rather than a voice note.
  voice: false,
};

export const HOME_TYPED = 'Her full name is Margaret Ellen Doyle';
export const HOME_TYPED_UNSURE = 'I need to sort out her paperwork';

/** What they typed was one of the things on the list: ticked, and said so. */
const HOME_SENT: ChatQuestion = {
  id: 'home-sent',
  says: ["Thanks, I've got her full name."],
  card: {
    label: HOME_CONCEPT_2.todo,
    sentence: true,
    items: HOME_CONCEPT_2.todos,
    checked: 1,
    found: ['Margaret Ellen Doyle'],
  },
  ask: 'Send the rest whenever you like, or tell me what else is on your mind.',
  mode: 'say',
};

/**
 * Not sure: the list pins itself while Keni asks, and lets go once they
 * answer.
 */
const HOME_CHECK: ChatQuestion = {
  id: 'home-check',
  card: {
    label: HOME_CONCEPT_2.todo,
    sentence: true,
    items: HOME_CONCEPT_2.todos,
    checklistUntil: 'home-check',
  },
  ask: "Are you sending any of these now? If not, I'll put the list away. You can ask for it any time.",
  mode: 'say',
  quickReplies: [
    { label: "Yes, I'm sending them" },
    { label: 'Put it away', secondary: true },
  ],
  answer: 'Put it away',
};

const HOME_PUT_AWAY: ChatQuestion = {
  id: 'home-put-away',
  says: ['Done. Just ask whenever you want it back.'],
  ask: 'So, her paperwork. What do you need to sort out?',
  mode: 'say',
};

export const INTAKE_CHAT_V2_0_HOME_SENT: ChatQuestion[] = [
  { ...HOME_TYPING, answer: HOME_TYPED },
  HOME_SENT,
];

export const INTAKE_CHAT_V2_0_HOME_UNSURE: ChatQuestion[] = [
  { ...HOME_TYPING, answer: HOME_TYPED_UNSURE },
  HOME_CHECK,
  HOME_PUT_AWAY,
];

/**
 * Concept 4: what they could get, in place of a bare list of things to send,
 * so the list has a reason. How far each one is comes as a per cent, and
 * tapping it opens what is still needed for it.
 */
export const HOME_CONCEPT_4 = {
  heading: `What ${RECIPIENT} could get`,
  items: [
    {
      title: 'Attendance Allowance',
      detail: '£76.70 to £114.60 a week',
      icon: 'sterling-sign',
      progress: 60,
    },
    {
      title: 'Benefits calculator',
      detail: 'See what else she could get',
      icon: 'calculator',
    },
  ],
};

/**
 * The Attendance Allowance claim in the parts a family would recognise. The
 * DWP form has seventeen sections (the call-to-claim schema); these group
 * them: 1 about the person, 2 signing for someone else, 3 illnesses, GP and
 * consent, 8 hospital, 10 payment, and 4 to 7 care needs day and night, last
 * because it is the biggest and the one where they just talk. The statement,
 * declaration and constant attendance sections come at the end, or rarely
 * apply, so they are not parts here.
 */
export const AA_CLAIM: ClaimPart[] = [
  {
    title: `About ${RECIPIENT}`,
    short: 'Her',
    line: 'Her name, date of birth, National Insurance number and address',
    needs: [
      'Her full name',
      'Date of birth',
      'National Insurance number',
      'Address',
      'Phone number',
    ],
  },
  {
    title: 'About you',
    short: 'You',
    line: "That you're filling it in for her, and why",
    needs: ["That you're filling it in for her", "Why she can't do it herself"],
  },
  {
    title: 'Her health',
    short: 'Health',
    line: 'Her conditions, her GP and anyone else she sees',
    needs: [
      'Her conditions',
      'Her GP',
      "Specialists she's seen this year",
      'Any reports or tests',
      'Her home, like where the toilet is',
    ],
  },
  {
    title: 'Hospital or care home',
    short: 'Hospital',
    line: "If she's in one now, or came out in the last six weeks",
    needs: ['Where she is or was', 'When she went in', 'When she came out'],
  },
  {
    title: 'Getting paid',
    short: 'Payment',
    line: 'The account the money goes into',
    needs: [
      'Account holder',
      'Bank or building society',
      'Sort code and account number',
    ],
  },
  {
    title: 'Her care, day and night',
    short: 'Care',
    line: 'What help she needs, in your own words',
    needs: [
      'Getting up and going to bed',
      'Washing and dressing',
      'Going to the toilet',
      'Eating and medication',
      'Getting around',
      'Being watched over',
      'Help at night',
      'When she started needing help',
    ],
  },
];

/** The same, with her GP in: what concept 4.2 has been told. */
const AA_CLAIM_GP: ClaimPart[] = AA_CLAIM.map((part) =>
  part.short === 'Health'
    ? { ...part, found: ['', 'Dr Aisha Kerr, Brae Medical Practice'] }
    : part,
);

/**
 * Concept 4.1: Attendance Allowance tapped opens a conversation about it.
 * Keni says what it is, then what the claim will need to know, as a preview:
 * no states and no per cent, nothing yet to be behind on. Each part opens to
 * what it asks.
 */
const AA_CHAT: ChatQuestion = {
  id: 'aa',
  says: [
    "Attendance Allowance is money for people over State Pension age who need help with personal care, like washing, dressing or being watched over at night. It isn't means-tested, so her savings and income don't count.",
  ],
  card: {
    label: "Here's what we'll need to know",
    sentence: true,
    claim: AA_CLAIM,
  },
  ask: "Start wherever you like. Tap a part to see what we'll ask, or just tell me about her.",
  mode: 'say',
  // For anyone who would rather be led.
  quickReplies: [{ label: 'Where should I start?' }],
  answer: 'Her GP is Dr Aisha Kerr at Brae Medical Practice',
  next: { 'Where should I start?': 'aa-lead' },
};

/**
 * Concept 4.2: what they said was about a part nobody had started, so that
 * part starts. Keni names it, and the card opens on that part, with what came
 * in ticked and the rest of what it needs under it. All is a tap away.
 */
const AA_STARTED: ChatQuestion = {
  id: 'aa-started',
  says: ["Thanks. That's her health, so I've started that part."],
  card: {
    label: 'Your claim',
    sentence: true,
    claim: AA_CLAIM_GP,
    claimView: 2,
  },
  ask: 'Has she seen anyone else about her dementia this year, like a specialist or a memory clinic?',
  mode: 'say',
  quickReplies: [
    { label: 'Yes' },
    { label: 'No, just her GP', secondary: true },
  ],
};

/** Concept 4.3: the same, with All tapped: every part, and how far each is. */
const AA_STARTED_ALL: ChatQuestion = {
  ...AA_STARTED,
  id: 'aa-started-all',
  card: { ...AA_STARTED.card!, claimView: 'all' },
};

/** Where should I start: Keni picks the easiest, then works up to her care. */
const AA_LEAD: ChatQuestion = {
  id: 'aa-lead',
  ask: "Let's start with her health, since you'll know most of it already. Who's her GP?",
  mode: 'say',
};

export const INTAKE_CHAT_V2_0_AA: ChatQuestion[] = [
  AA_CHAT,
  AA_STARTED,
  // Off the main path.
  AA_LEAD,
];

/** For concept 4.3: the card switched back to All. */
export const INTAKE_CHAT_V2_0_AA_ALL: ChatQuestion[] = [
  AA_CHAT,
  AA_STARTED_ALL,
];
